import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Check, CreditCard, Zap, ArrowRight, Loader2, MapPin, Bell, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { TierBadge } from '../components/UpgradePrompt';

const plans = [
  {
    id: 'individual',
    name: 'Individual',
    description: 'For individual property managers',
    pricing: [
      { term: '1 Year', price: 99, planId: 'individual_1yr', highlight: false },
      { term: '2 Year', price: 79, planId: 'individual_2yr', highlight: false },
      { term: '3 Year', price: 49, planId: 'individual_3yr', highlight: true },
    ],
    datasets: ['DOB Violations'],
    features: [
      '10 searches per day',
      'NYC data only',
      'Basic PDF reports',
      'Single user',
    ]
  },
  {
    id: 'pro',
    name: 'Teams',
    description: 'For growing portfolios & teams',
    pricing: [
      { term: 'Monthly', price: 149, planId: 'pro_monthly', highlight: false },
      { term: 'Quarterly', price: 399, planId: 'pro_quarterly', highlight: false },
      { term: 'Yearly', price: 1399, planId: 'pro_yearly', highlight: true },
    ],
    datasets: ['DOB Violations', 'Fire Dept', 'ECB', 'HPD'],
    features: [
      '50 searches per day',
      'All city data access',
      'Enhanced PDF reports (maps, flood)',
      'Property watchlist (25)',
      'Email monitoring alerts',
      'AI Chat Assistant',
      'Up to 5 team members',
    ],
    featured: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    pricing: [
      { term: '1 Year', price: 699, planId: 'enterprise_1yr', highlight: false },
      { term: '2 Year', price: 599, planId: 'enterprise_2yr', highlight: false },
      { term: '3 Year', price: 499, planId: 'enterprise_3yr', highlight: true },
    ],
    datasets: ['All 15+ datasets included'],
    features: [
      'Unlimited searches',
      'AI Insite reports',
      'Scheduled reports',
      'Unlimited watchlists',
      'CRM/API access',
      'Unlimited team members',
      'White-label branding',
      'Dedicated support',
    ]
  }
];

export default function BillingPage() {
  const { user, api, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // City expansion preferences
  const [cityPrefs, setCityPrefs] = useState({ subscribed_cities: [], notify_all_new_cities: false, available_cities: [] });
  const [savingCityPrefs, setSavingCityPrefs] = useState(false);

  // Check for payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Fetch current subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await api().get('/subscription/current');
        setSubscription(res.data);
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      }
    };
    fetchSubscription();
  }, [api]);

  // Fetch city preferences
  useEffect(() => {
    const fetchCityPrefs = async () => {
      try {
        const res = await api().get('/user/city-preferences');
        setCityPrefs(res.data);
      } catch (err) {
        console.error('Failed to fetch city preferences:', err);
      }
    };
    fetchCityPrefs();
  }, [api]);

  const toggleCitySubscription = (cityId) => {
    setCityPrefs(prev => ({
      ...prev,
      subscribed_cities: prev.subscribed_cities.includes(cityId)
        ? prev.subscribed_cities.filter(id => id !== cityId)
        : [...prev.subscribed_cities, cityId]
    }));
  };

  const toggleNotifyAll = () => {
    setCityPrefs(prev => ({
      ...prev,
      notify_all_new_cities: !prev.notify_all_new_cities
    }));
  };

  const saveCityPreferences = async () => {
    setSavingCityPrefs(true);
    try {
      await api().post('/user/city-preferences', {
        city_ids: cityPrefs.subscribed_cities,
        notify_all_new_cities: cityPrefs.notify_all_new_cities
      });
      toast.success('City notification preferences saved!');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingCityPrefs(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const res = await api().get(`/subscription/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        setCheckingPayment(false);
        await refreshUser();
        window.history.replaceState({}, '', '/billing');
        const subRes = await api().get('/subscription/current');
        setSubscription(subRes.data);
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment status check failed:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSelectPlan = async (planId) => {
    setLoading(planId);

    try {
      const res = await api().post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to start checkout. Please try again.';
      toast.error(errorMsg);
      setLoading(null);
    }
  };

  const currentPlanId = user?.tier;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="billing-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Billing & Subscription</h1>
          <p className="text-zinc-400">Manage your subscription and payment methods</p>
        </div>

        {/* Payment Processing Overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="glass-card p-8 rounded-xl text-center">
              <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Processing Payment</h3>
              <p className="text-zinc-400">Please wait while we confirm your payment...</p>
            </div>
          </div>
        )}

        {/* Current Plan */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400 mb-1">Current Plan</p>
              <h3 className="text-xl font-bold text-white capitalize">{user?.tier} Plan</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Access to: {user?.datasets_access?.map(d => d.replace(/_/g, ' ')).join(', ')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-indigo-400" />
              <span className="text-indigo-400 font-medium">Active</span>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlanId === plan.id;
            
            return (
              <div 
                key={plan.id}
                className={`glass-card rounded-xl p-6 relative ${
                  plan.featured ? 'border-indigo-500/50' : ''
                } ${isCurrentPlan ? 'ring-2 ring-emerald-500/50' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-emerald-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-zinc-400 text-sm mb-4">{plan.description}</p>
                
                {/* Pricing Options */}
                <div className="mb-4 space-y-2">
                  {plan.pricing.map((tier, i) => (
                    <div 
                      key={i} 
                      className={`flex justify-between items-center p-3 rounded-lg ${
                        tier.highlight ? 'bg-indigo-600/20 border border-indigo-500/30' : 'bg-white/5'
                      }`}
                    >
                      <div>
                        <span className="text-sm text-zinc-300">{tier.term}</span>
                        {tier.highlight && (
                          <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                            Best Value
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-white">${tier.price}/mo</span>
                        <Button
                          size="sm"
                          onClick={() => handleSelectPlan(tier.planId)}
                          disabled={isCurrentPlan || loading === tier.planId}
                          className={`${tier.highlight ? 'btn-primary' : 'btn-secondary'} px-3 py-1 text-xs`}
                          data-testid={`select-${tier.planId}-btn`}
                        >
                          {loading === tier.planId ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Select'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Datasets</p>
                  <div className="flex flex-wrap gap-1">
                    {plan.datasets.map((ds, i) => (
                      <span key={i} className="badge-info text-xs">{ds}</span>
                    ))}
                  </div>
                </div>

                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* City Expansion Notifications */}
        <div className="glass-card rounded-xl p-6" data-testid="city-expansion-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg">
              <Globe className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Expanding to New Cities</h3>
              <p className="text-sm text-zinc-400">Get notified when we launch in additional markets</p>
            </div>
          </div>

          {/* Current Coverage */}
          <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-white">New York City</p>
                <p className="text-sm text-emerald-400">Currently Available — Your subscription includes full NYC coverage</p>
              </div>
            </div>
          </div>

          {/* Notify All Toggle */}
          <div 
            className={`mb-6 p-4 rounded-lg border cursor-pointer transition-all ${
              cityPrefs.notify_all_new_cities 
                ? 'bg-indigo-500/10 border-indigo-500/50' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
            }`}
            onClick={toggleNotifyAll}
            data-testid="notify-all-cities-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className={`w-5 h-5 ${cityPrefs.notify_all_new_cities ? 'text-indigo-400' : 'text-zinc-400'}`} />
                <div>
                  <p className={`font-medium ${cityPrefs.notify_all_new_cities ? 'text-white' : 'text-zinc-300'}`}>
                    Notify me when new cities launch
                  </p>
                  <p className="text-sm text-zinc-500">Be the first to know when we expand to additional markets</p>
                </div>
              </div>
              <div className={`w-12 h-6 rounded-full transition-colors ${
                cityPrefs.notify_all_new_cities ? 'bg-indigo-500' : 'bg-zinc-700'
              }`}>
                <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-transform ${
                  cityPrefs.notify_all_new_cities ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </div>
            </div>
          </div>

          {/* Upcoming Cities - Interest Registration */}
          <div className="mb-4">
            <p className="text-sm text-zinc-400 mb-3">Select cities you're interested in to receive priority launch notifications:</p>
          </div>

          {/* City Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {cityPrefs.available_cities?.map((city) => {
              const isSubscribed = cityPrefs.subscribed_cities.includes(city.id);
              return (
                <div
                  key={city.id}
                  onClick={() => toggleCitySubscription(city.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    isSubscribed
                      ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border-indigo-500/50'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`city-${city.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <MapPin className={`w-4 h-4 ${isSubscribed ? 'text-indigo-400' : 'text-zinc-500'}`} />
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      isSubscribed ? 'bg-indigo-500 border-indigo-500' : 'border-zinc-600'
                    }`}>
                      {isSubscribed && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <p className={`font-medium ${isSubscribed ? 'text-white' : 'text-zinc-300'}`}>
                    {city.name}
                  </p>
                  <p className="text-xs text-zinc-500">{city.state}</p>
                  <div className="mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      city.status === 'coming_soon' 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : city.status === 'planned'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {city.status === 'coming_soon' ? 'Coming Soon' : city.status === 'planned' ? 'Planned' : 'Researching'}
                    </span>
                    <span className="text-[10px] text-zinc-600 ml-2">{city.eta}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              {cityPrefs.subscribed_cities.length > 0 || cityPrefs.notify_all_new_cities
                ? `You'll be notified when ${cityPrefs.notify_all_new_cities ? 'any new city launches' : `${cityPrefs.subscribed_cities.length} ${cityPrefs.subscribed_cities.length === 1 ? 'city launches' : 'cities launch'}`}`
                : 'Select cities to receive launch notifications'}
            </p>
            <Button
              onClick={saveCityPreferences}
              disabled={savingCityPrefs}
              className="btn-primary"
              data-testid="save-city-prefs-btn"
            >
              {savingCityPrefs ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Payment History */}
        {subscription?.last_payment && (
          <div className="glass-card rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Payment History</h3>
            <div className="flex items-center justify-between py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-white">{subscription.last_payment.plan_id?.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-zinc-400">
                    {new Date(subscription.last_payment.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-medium">${subscription.last_payment.amount}</p>
                <span className="badge-success">Completed</span>
              </div>
            </div>
          </div>
        )}

        {/* Running Ticker Banner */}
        <div className="mt-8 overflow-hidden rounded-lg bg-gradient-to-r from-[#1B3C6B]/50 via-[#0D1B2A]/80 to-[#1B3C6B]/50 border border-[#1B3C6B]/30">
          <div className="py-3 animate-marquee whitespace-nowrap">
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
            <span className="mx-8 text-lg font-semibold">
              <span className="text-white">Now Serving </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">New York City</span>
              <span className="text-zinc-500 mx-4">•</span>
            </span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
